'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'
import RiskQuizInlineNudge from '@/app/components/blog/RiskQuizInlineNudge'
import KeyStats from '@/app/components/blog/KeyStats'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Are Cans Lined With Plastic? Food, Beer and Tuna Checked"
      author="MicroPlastics Research Desk"
      date="July 13, 2026"
      readTime="12 min read"
      heroImage="/blog/heroes/food.webp"
      heroImageAlt="Are cans lined with plastic, what food, beer and tuna can linings are actually made of"
    >
      <BlogLastUpdated iso="2026-07-13" formatted="July 13, 2026" />

      <BlogTLDR>
        <strong>Yes. Essentially every metal food and beverage can has a
        polymer lining on the inside.</strong> It has to: bare steel and
        aluminium corrode on contact with food acids, and food stored against
        raw metal picks up a metallic taste and dissolved metal. The coating is
        a real plastic film, roughly 5–12 micrometres thick, sprayed on and
        baked. The interesting question is <em>which</em> polymer, and that
        answer changed. Cans were historically lined with a{' '}
        <strong>BPA-based epoxy resin</strong>. The Can Manufacturers Institute
        now says roughly <strong>98% of US food can production has moved to new
        linings</strong>, acrylic, polyester, non-BPA epoxies, or olefin
        polymers, and independent testing broadly agrees. That is genuine
        progress on BPA. But <strong>&ldquo;BPA-free&rdquo; is not
        &ldquo;plastic-free.&rdquo;</strong> A polymer film is still a polymer
        film, the replacements bring their own migrating chemistry, and the
        highest-migration case is unchanged: <strong>acidic food, tomatoes,
        citrus, pickles, in a can, for a long time.</strong>
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Every metal food and drink can is lined with a sprayed-on polymer coating, typically <strong>5–12 µm thick</strong>. Without it the can corrodes and the food tastes of metal. There is no such thing as an uncoated modern food can.</>,
          <>The historical lining is a <strong>BPA-based epoxy</strong> (BPA + epichlorohydrin → BADGE resin). The Can Manufacturers Institute says <strong>~98% of US food can production has moved off it</strong>, to acrylic, polyester, non-BPA epoxy or olefin linings. Independent testing found ~95% of cans BPA-free by 2019.</>,
          <>The <strong>EFSA 2023 re-evaluation cut the tolerable daily intake for BPA to 0.2 ng/kg body weight per day (about 20,000× lower</strong> than its previous provisional figure) and concluded that average and high consumers in every age group exceed it. The EU has since banned BPA in food-contact coatings outright.</>,
          <>On the &ldquo;regrettable substitution&rdquo; question, be precise: <strong>BPS and BPF are demonstrably as hormonally active as BPA</strong>, but the US can industry states it does <em>not</em> intentionally add them to linings. The replacements in cans are acrylics, polyesters and non-BPA epoxies, which bring different, less-studied migrants (polyester oligomers), not necessarily safer ones.</>,
          <><strong>Beer and soda cans are the same story in aluminium:</strong> two-piece drawn aluminium bodies with an internal spray coating. The beverage side is moving to bisphenol-free coatings too, but there is no public market-basket survey for beverage cans the way there is for food cans.</>,
          <>Honest limit: <strong>we could not find a published study that counts microplastic particles shed from a metal can lining into food.</strong> The chemical migration evidence is strong and specific. The particle evidence for can linings specifically is essentially absent, and we are not going to invent a number to fill the gap.</>,
        ]}
      />

      <RiskQuizInlineNudge source="blog_inline_can_linings" />

      <KeyStats
        heading="Can linings, the numbers that are actually verified"
        stats={[
          { value: '5–12 µm', label: 'thickness of the internal can coating', context: 'a sprayed, baked polymer film applied to both the inside and outside of food and beverage cans', source: 'Food Packaging Forum, Can Coatings', sourceUrl: 'https://foodpackagingforum.org/resources/background-articles/can-coatings' },
          { value: '~98%', label: 'of US food can production on non-BPA linings', context: 'CMI’s figure; its 2019 Canned Food Market Basket Report found nearly 100% of sampled cans free of BPA in the lining', source: 'Can Manufacturers Institute', sourceUrl: 'https://www.cancentral.com/innovations-food-cans/' },
          { value: '0.2 ng/kg/day', label: 'EFSA’s 2023 tolerable daily intake for BPA', context: 'around 20,000× lower than its 2015 provisional TDI of 4 µg/kg/day; EFSA found average and high consumers in all age groups exceed it', source: 'EFSA, 2023', sourceUrl: 'https://www.efsa.europa.eu/en/news/bisphenol-food-health-risk' },
          { value: '1,221%', label: 'rise in urinary BPA from canned soup', context: 'randomised crossover trial: five days of one canned soup a day vs. fresh soup, 84 participants, the single cleanest demonstration that can linings migrate into people', source: 'Carwile et al., JAMA 2011', sourceUrl: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC3367259/' },
          { value: '16 of 20', label: 'canned sardine brands with zero plastic particles', context: 'micro-Raman analysis of 20 brands from 13 countries; the other four had 1–3 particles each, a useful corrective to claims that all canned seafood is loaded with plastic', source: 'Karami et al., Sci. Total Environ. 2018', sourceUrl: 'https://www.sciencedirect.com/science/article/abs/pii/S0048969717323471' },
          { value: '20 July 2026', label: 'EU deadline for BPA in food-contact coatings', context: 'Regulation (EU) 2024/3190 bans BPA in varnishes and coatings; cans preserving fruit, vegetables and fishery products get an extension into 2028', source: 'Regulation (EU) 2024/3190', sourceUrl: 'https://eur-lex.europa.eu/eli/reg/2024/3190/oj/eng' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why the lining exists at all
      </h2>
      <p>
        Start with the engineering, because it explains everything that
        follows. A food can is a metal pressure vessel that gets filled with a
        wet, salty, often acidic substance and then cooked, <strong>retort
        sterilisation heats the sealed can to around 120°C</strong>, and then
        left on a shelf for two or three years. Bare tinplate steel or bare
        aluminium cannot survive that. The metal corrodes, hydrogen builds up,
        the can swells, and long before any of that happens the food tastes
        metallic and picks up dissolved metal.
      </p>
      <p>
        So the inside of the can is coated. The coating is a liquid polymer
        resin, sprayed onto the metal and cured in an oven into a continuous
        film about <strong>5 to 12 micrometres thick</strong>, around a tenth
        the thickness of a human hair. It is applied to the inside and, usually,
        the outside too. Food cans are mostly <strong>three-piece welded
        tinplate steel</strong>; beverage cans are mostly{' '}
        <strong>two-piece drawn-and-ironed aluminium</strong>. Both get an
        internal coating. There is no meaningful category of &ldquo;uncoated
        can&rdquo; in a Western supermarket.
      </p>
      <p>
        That is the honest core of the answer to the question in the title.{' '}
        <strong>Yes, cans are lined with plastic, all of them, by
        necessity.</strong> Anyone selling you a &ldquo;plastic-free can&rdquo;
        is selling you a glass jar.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What the lining is made of, the five chemistries
      </h2>
      <p>
        Here is where the last decade of change lives. Until roughly 2010, the
        default was an <strong>epoxy resin made by reacting BPA with
        epichlorohydrin</strong>, which produces BADGE, bisphenol A diglycidyl
        ether, the backbone of the classic gold-coloured can lining. Epoxy is
        genuinely excellent at the job: tough, flexible enough to survive the
        can being formed, and chemically resistant to almost any food.
      </p>
      <p>
        Consumer pressure, then regulation, pushed the industry off it. The
        replacements are not one material but four families, each with its own
        weaknesses, which is precisely why no single one took over.
      </p>

      <BlogComparisonTable
        caption="What metal cans are actually lined with (2026)"
        columns={[
          { key: 'lining', label: 'Lining type' },
          { key: 'what', label: 'What it is' },
          { key: 'where', label: 'Where it is used' },
          { key: 'known', label: 'What we know about it' },
          { key: 'verdict', label: 'Verdict' },
        ]}
        rows={[
          {
            lining: 'BPA epoxy (BADGE)',
            what: 'BPA + epichlorohydrin, cured into an epoxy film. The classic gold lining.',
            where: 'Historically the default. Now ~2% of US food can production; still present in some imported and speciality cans.',
            known: 'Best-studied lining by far. BPA and BADGE migrate into food and are measurable in urine within days of eating canned food. EFSA cut the BPA tolerable intake ~20,000× in 2023.',
            verdict: 'The one to avoid, and the one the EU has now banned.',
          },
          {
            lining: 'Non-BPA epoxy',
            what: 'Epoxy chemistry built on a bisphenol other than BPA, or on non-bisphenol starting materials.',
            where: 'One of the four families CMI names as standard replacements.',
            known: 'Performance close to BPA epoxy. Composition is generally not disclosed on-pack, so "non-BPA epoxy" tells you what it is not, rather than what it is.',
            verdict: 'Better than BPA epoxy. Opaque.',
          },
          {
            lining: 'Acrylic',
            what: 'Acrylic resin, typically from ethyl acrylate. Clean-looking, resists sulphide staining.',
            where: 'Widely used; roughly half the can-coating market by revenue because it costs about twice as much as epoxy or polyester.',
            known: 'Brittle relative to epoxy and can affect taste and odour. No bisphenol backbone. Little independent particle-release data.',
            verdict: 'A real improvement on BPA. Thinly studied.',
          },
          {
            lining: 'Polyester',
            what: 'Polyester resin built from isophthalic and terephthalic acid, the same acid family as PET.',
            where: 'One of the most common BPA-free replacements in both food and beverage cans.',
            known: 'Adheres well to metal but is the weakest of the group under acidic conditions. Shown to release cyclic polyester oligomers into food simulants and infant food, a different set of migrants, largely without toxicological data or migration limits.',
            verdict: 'BPA-free, but it swaps one migration question for a newer one.',
          },
          {
            lining: 'Oleoresin',
            what: 'A plant-derived oil-and-resin mixture, the pre-epoxy technology, revived.',
            where: 'Niche. Some natural-food brands reverted to it specifically to escape bisphenols.',
            known: 'Flexible but adheres poorly to metal and gives weaker corrosion protection, which limits it to milder, low-acid foods.',
            verdict: 'The closest thing to a non-synthetic lining. Not usable for the hard cases.',
          },
        ]}
      />
      <p className="text-sm text-gray-500">
        A fifth family, <strong>PVC-based (vinyl/organosol)</strong> coatings,
        also shows up in can testing. It is acid-resistant and flexible, but it
        is made from vinyl chloride (a known human carcinogen) and needs
        plasticisers, so it is the one replacement that is hard to call an
        upgrade. It is not among the four families the industry names as its
        standard non-BPA linings.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How much of the US canned-food supply actually moved off BPA?
      </h2>
      <p>
        Most of it, and this is one of the few consumer-chemistry stories where
        the pressure campaign genuinely worked. The{' '}
        <strong>Can Manufacturers Institute</strong> states plainly that
        &ldquo;new linings&hellip;do not use bisphenol A (BPA) as a component of
        the can lining,&rdquo; that <strong>nearly all (about 98%) of US food
        can production has moved to new linings</strong>, and that its 2019
        Canned Food Market Basket Report found nearly 100% of sampled cans free
        of BPA. That is a trade body describing its own members, so treat it as
        a claim, not a measurement.
      </p>
      <p>
        The independent numbers point the same way. The{' '}
        <strong>Center for Environmental Health</strong>, which had spent years
        publicly testing cans and naming brands, reported a steep decline
        between 2017 and 2019, with <strong>about 95% of cans testing free of
        BPA</strong> in its 2019 round. For comparison, the 2016{' '}
        <em>Buyer Beware</em> coalition study, nearly 200 cans across the US and
        Canada, still found BPA-based epoxy in the majority of them. The shift
        in under a decade is real.
      </p>
      <p>
        Two caveats worth carrying. First, <strong>the transition is a US
        food-can story.</strong> Imported cans, older stock, and some
        speciality products still carry epoxy. Second, and more important for
        this article: moving off BPA did not remove the plastic. It replaced one
        polymer film with a different polymer film. Which brings us to the
        substitution question.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The regrettable-substitution problem, stated precisely
      </h2>
      <p>
        You have read the shorthand a hundred times: <em>BPA-free cans just use
        BPS instead, and BPS is just as bad.</em> Half of that is right, and the
        half that is wrong matters.
      </p>
      <p>
        The chemistry half is right. A systematic review in{' '}
        <em>Environmental Health Perspectives</em> compared BPA with its two
        commonest substitutes and found that <strong>BPS and BPF are of the same
        order of potency and the same mode of action as BPA</strong>, oestrogenic, anti-oestrogenic, androgenic and anti-androgenic. They are
        structural analogues, and they behave like it. Later in-vitro test
        batteries have reached the same conclusion across a wider panel of
        bisphenol analogues. Where BPS and BPF <em>are</em> used, thermal
        receipt paper, some polycarbonate replacements, calling the swap
        &ldquo;regrettable&rdquo; is fair.
      </p>
      <p>
        The can-specific half is where the shorthand breaks down. The{' '}
        <strong>US can industry states explicitly that it does not intentionally
        add BPF, BPB or BPS to can linings</strong>, and the four families it
        names as replacements, acrylic, polyester, non-BPA epoxy, olefin
        polymers, are not bisphenol-S chemistry. So the honest position is
        this: <strong>the BPS-in-cans claim is not supported by what the can
        industry says it uses, and we are not going to repeat it as though it
        were.</strong>
      </p>
      <p>
        That is not the same as saying the replacements are proven safe. The
        real problem is subtler and, in our view, more concerning:{' '}
        <strong>the replacements are less studied, and they bring their own
        migrants.</strong> Polyester linings have been shown to release{' '}
        <strong>cyclic polyester oligomers</strong> into food simulants and into
        infant food, compounds for which, as one 2023 analysis noted, no
        toxicological data exist and no migration limits have been set.
        Meanwhile, the Center for Environmental Health&apos;s own summary is
        blunt: adequate testing to establish that the new linings are safe for
        food has not been done, and only a couple of companies disclose which
        liner they moved to. We swapped a well-characterised bad actor for a set
        of poorly characterised unknowns. That is progress, but it is not a
        clean win, and you should hold it loosely. Our{' '}
        <Link href="/blog/is-bpa-free-plastic-safe" className="text-[#1E6B47] underline hover:text-[#155235]">BPA-free deep dive</Link>{' '}
        and{' '}
        <Link href="/blog/microplastics-vs-pfas-vs-bpa" className="text-[#1E6B47] underline hover:text-[#155235]">microplastics vs PFAS vs BPA explainer</Link>{' '}
        go further on why &ldquo;free of X&rdquo; labels tell you so little.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Do can linings actually shed microplastics? The honest answer
      </h2>
      <p>
        This is a microplastics site, so let us be exact about what is known and
        what is not, because the internet, including, in places, our own older
        coverage, has run ahead of the evidence here.
      </p>
      <p>
        <strong>What is well established: chemical migration.</strong> BPA and
        BADGE demonstrably move from epoxy linings into food, at levels that
        show up in human urine within days. The Carwile trial at Harvard is the
        cleanest demonstration: 84 people, five days of one canned soup a day
        versus fresh soup, and a <strong>1,221% rise in urinary BPA</strong>.
        That is not a modelling exercise or a lab simulant, it is a randomised
        crossover trial in people, and it is why the EFSA re-evaluation matters
        so much for canned food specifically.
      </p>
      <p>
        <strong>What is plausible but unmeasured: particle release.</strong> A
        can lining is a polymer film in intimate contact with wet, sometimes
        acidic food, cooked at 120°C during sterilisation and then held for
        years. Every mechanism we know for microplastic release from food-contact
        polymers (heat, contact time, acidity, oil) is present. So the
        expectation that linings shed particles is entirely reasonable.
      </p>
      <p>
        <strong>And yet: we could not find a single published study that counts
        microplastic particles released from a metal can lining into food.</strong>{' '}
        The 2025 systematic evidence map in <em>npj Science of Food</em>, the
        most complete survey of this question that exists, screening the
        literature and extracting 600 data entries from 103 eligible studies, is dominated by <strong>bottles (31% of entries), unspecified containers
        (19%), tea bags (12%) and cups (10%)</strong>. Cans do not feature among
        the studied categories. The authors also note that most of the studies
        they included are of low quality. When you see a confident particle count
        for can linings online, check the citation: it usually leads back to a
        study of plastic containers or bottles, not cans.
      </p>
      <p>
        The one direct measurement that does exist points <em>down</em>, not up.
        Karami and colleagues ran micro-Raman analysis on{' '}
        <strong>20 brands of canned sardines and sprats from 13 countries</strong>{' '}
        and found <strong>no plastic particles at all in 16 of them</strong>, and
        1–3 particles each in the other four. They concluded the health risk from
        micro- and mesoplastics in canned sardines was limited. That study only
        detected particles above ~149 µm, so it says nothing about the small
        stuff, but it is the best direct evidence we have, and it does not
        support the idea that a can is a particle firehose.
      </p>
      <p>
        So the position we will defend: <strong>the chemical case against can
        linings is strong and specific; the particle case is theoretically sound
        and empirically empty.</strong> Anyone giving you a number of
        microplastics per can is making it up. We would rather tell you the
        evidence is thin than manufacture a statistic to match the headline.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Can type by can type, food, beer, tuna, tomatoes, cartons
      </h2>
      <p>
        The practical variation is not really between can <em>types</em>, it is
        between <strong>contents</strong>. Acid and fat and time are what pull
        chemistry out of a polymer. Here is how the common cases sort out.
      </p>

      <BlogComparisonTable
        caption="Can and carton types: what is inside, and how much it matters"
        columns={[
          { key: 'type', label: 'Container' },
          { key: 'metal', label: 'Body' },
          { key: 'lining', label: 'Lining' },
          { key: 'risk', label: 'Migration picture' },
        ]}
        rows={[
          {
            type: 'Food cans (beans, corn, soup)',
            metal: 'Three-piece welded tinplate steel',
            lining: 'Acrylic, polyester, non-BPA epoxy or olefin, ~98% of US production now non-BPA',
            risk: 'Baseline. Low-acid contents like beans and corn are the mildest case for any lining. Rinsing drained beans is a free, sensible habit.',
          },
          {
            type: 'Beer & soda cans',
            metal: 'Two-piece drawn-and-ironed aluminium',
            lining: 'Internal spray coating, historically epoxy; suppliers now sell bisphenol-free lines (AkzoNobel Accelshield, PPG Innovel Pro)',
            risk: 'Same principle, less transparency. There is no beverage-can equivalent of the food-can market-basket survey, so the BPA-free share of US beer and soda cans is not publicly quantified. Carbonation makes the contents mildly acidic.',
          },
          {
            type: 'Tuna & sardine cans',
            metal: 'Tinplate steel',
            lining: 'Same non-BPA families; some brands publicise BPA-NI linings',
            risk: 'Two sources, one container: the fish carries whatever it carried, and the lining does what linings do. Oil-packed fish extracts more from the coating than brine-packed. But the only direct particle study on canned sardines found nothing in 16 of 20 brands.',
          },
          {
            type: 'Tomatoes & acidic foods (citrus, pickles)',
            metal: 'Tinplate steel',
            lining: 'Same families, but polyester specifically performs worst under acid',
            risk: 'The highest-migration case, and the most actionable line in this article. Acid attacks the coating; canned tomato products have consistently shown the highest bisphenol levels of any category. The EU knows it: the BPA ban gave fruit, vegetable and fish cans the longest extension precisely because the alternatives struggle there.',
          },
          {
            type: 'Aseptic cartons (Tetra Pak, milk cartons)',
            metal: 'Paperboard + aluminium foil',
            lining: 'Polyethylene, inner and outer layers, roughly 25% of the pack by weight',
            risk: 'Yes, milk and juice cartons are lined with plastic too: a six-layer laminate whose innermost food-contact surface is PE. No bisphenols, no epoxy, no retort sterilisation at 120°C, which is why cartons are usually the better swap for acidic liquids. Not plastic-free. Just a different, milder plastic problem.',
          },
        ]}
      />
      <p className="text-sm text-gray-500">
        For what is <em>in</em> the food rather than what the container is made
        of, see{' '}
        <Link href="/blog/microplastics-in-canned-food" className="text-[#1E6B47] underline hover:text-[#155235]">microplastics in canned food</Link>,{' '}
        <Link href="/blog/microplastics-in-canned-tuna-and-salmon" className="text-[#1E6B47] underline hover:text-[#155235]">microplastics in canned tuna and salmon</Link>, and{' '}
        <Link href="/blog/microplastics-in-beer-and-wine" className="text-[#1E6B47] underline hover:text-[#155235]">microplastics in beer and wine</Link>.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Where the regulators actually stand
      </h2>
      <p>
        The two big food-safety agencies disagree, and you should know that
        rather than be told a consensus exists.
      </p>
      <p>
        <strong>EFSA (Europe) moved decisively.</strong> Its 2023 re-evaluation
        of BPA, drawing on more than 800 studies published since 2013, set a
        tolerable daily intake of <strong>0.2 nanograms per kilogram of body
        weight per day</strong>, roughly <strong>20,000 times lower</strong>{' '}
        than the 4 µg/kg/day provisional figure it had used since 2015. The
        driver was immune effects: an increase in T-helper cells in the spleen,
        which EFSA linked to allergic lung inflammation and autoimmune
        conditions. Its conclusion was that consumers with average <em>and</em>{' '}
        high exposure, in <em>all</em> age groups, exceed the new TDI. The
        European Commission followed with{' '}
        <strong>Regulation (EU) 2024/3190</strong>, which bans BPA in
        food-contact varnishes and coatings; the general deadline for
        non-compliant coated articles is <strong>20 July 2026</strong>, with an
        extension into 2028 for cans preserving fruit, vegetables and fishery
        products, the acidic, aggressive contents the alternatives handle worst.
      </p>
      <p>
        <strong>The FDA has not moved.</strong> Its stated position remains that
        &ldquo;the available information continues to support the safety of BPA
        for the currently approved uses in food containers and packaging,&rdquo;
        based on a four-year review of over 300 studies completed in 2014. The
        FDA <em>did</em> remove BPA from baby bottles, sippy cups and infant
        formula packaging in 2012–13, but it is careful to say those amendments
        were based on <strong>abandonment of the use, not on safety</strong>. The
        agency has agreed to reconsider its BPA assessment; nothing has changed
        yet.
      </p>
      <p>
        Germany&apos;s BfR has publicly disagreed with EFSA&apos;s methodology,
        and a published critique argued the evidence did not support lowering the
        TDI. This is a live scientific dispute, not a settled one. What is not in
        dispute: canned food is one of the largest dietary sources of BPA
        exposure, and the two agencies&apos; positions on it are now separated by
        four orders of magnitude.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What to actually do about it
      </h2>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Move acidic foods out of cans first.</strong> If you change one
          thing, change this. Tomatoes, tomato paste, citrus, pickled vegetables:
          buy them in <strong>glass jars</strong>. Glass is inert, there is no
          lining, and acidic contents are the highest-migration case for every
          lining chemistry including the new ones. Passata and chopped tomatoes in
          glass are widely available and barely cost more.
        </li>
        <li>
          <strong>Cartons are a real step down, but they are not plastic-free.</strong>{' '}
          A Tetra Pak-style carton is paperboard, aluminium foil and{' '}
          <strong>polyethylene</strong>, the layer touching your food is PE.
          What it does not have is a bisphenol epoxy or a 120°C retort cook. For
          milk, stock, coconut milk and passata, carton beats can. It just is not
          zero.
        </li>
        <li>
          <strong>Frozen beats canned for vegetables.</strong> Frozen produce
          skips the retort sterilisation and the years of shelf contact entirely,
          and the packaging is a cold-contact bag. Cold suppresses migration; it
          is the lowest-temperature link in the whole food chain.
        </li>
        <li>
          <strong>Dried beans are the cheapest swap in this article.</strong> A
          bag of dried chickpeas costs less than the cans it replaces, involves
          no lining at all, and, soaked overnight, simmered, frozen in
          portions, is genuinely no harder than opening a tin, just less
          instant.
        </li>
        <li>
          <strong>Prefer brands that disclose their lining.</strong> This is the
          lever that actually works. Very few companies say which liner they moved
          to; a handful do, and they only do it because customers ask. If a brand
          will tell you it uses an acrylic or oleoresin lining, that is a
          meaningful signal, not because acrylic is proven safe, but because
          disclosure is the precondition for anyone ever finding out.
        </li>
        <li>
          <strong>Do not store food in an opened can.</strong> Once opened, the
          coating is exposed to air and the metal is exposed at the cut edge.
          Decant leftovers into glass. Two minutes, zero cost.
        </li>
      </ul>
      <p>
        None of this requires you to fear a tin of chickpeas. The point of the
        ladder is that the top rungs (acidic food, oily food, long storage) are
        where the evidence actually is, and those are exactly the ones with easy
        substitutes. Everything else is a judgement call you can make calmly. The
        same logic runs through{' '}
        <Link href="/blog/what-does-microwave-safe-actually-mean" className="text-[#1E6B47] underline hover:text-[#155235]">what &ldquo;microwave safe&rdquo; actually means</Link>{' '}
        and the wider{' '}
        <Link href="/microplastics-in-food" className="text-[#1E6B47] underline hover:text-[#155235]">microplastics in food hub</Link>.
      </p>

      <BlogWhatTheAppChecks
        items={[
          <>The container format you scan (steel food can, aluminium beverage can, aseptic carton, glass jar, pouch) and what each one implies about the food-contact surface.</>,
          <>Whether the brand publicly discloses its can lining (BPA-NI, acrylic, oleoresin, polyester), surfaced on the scan result where that disclosure exists.</>,
          <>The contents modifier that actually drives migration: acidity, fat content, and how long the product has been on a shelf.</>,
          <>A 0–100 risk score with the cited research behind it, so canned chickpeas and canned tomato paste get scored as the different scenarios they are.</>,
          <>The cleanest same-job alternative (glass jar, carton, frozen, dried) matched to the specific product you scanned.</>,
        ]}
      />

      <BlogAppCTA
        headline="Scan the can before it goes in the trolley"
        sub="Lining disclosure is patchy and buried. The app reads the product, tells you what the container is, flags whether the brand discloses its liner, and ranks the alternatives on the same shelf, in about three seconds."
        buttonLabel="Scan a can in the app"
      />

      <BlogFAQ
        items={[
          {
            question: 'Are cans lined with plastic?',
            answer:
              'Yes. Essentially every metal food and beverage can has an internal polymer coating, typically 5–12 micrometres thick, sprayed on and baked. Without it the metal corrodes on contact with food acids and the food picks up a metallic taste. Historically that lining was a BPA-based epoxy resin; most US food cans have now switched to acrylic, polyester, non-BPA epoxy or olefin polymer linings. There is no such thing as an uncoated modern food can, the only genuinely unlined container is a glass jar.',
          },
          {
            question: 'Are aluminum cans lined with plastic?',
            answer:
              'Yes. Aluminium beverage cans are two-piece drawn-and-ironed bodies with an internal spray coating, a polymer film applied to the inside of the can. The coating stops the drink corroding the aluminium and stops the aluminium tainting the drink. Historically these coatings were epoxy-based; coating suppliers such as AkzoNobel and PPG now sell bisphenol-free alternatives, but unlike food cans there is no public survey of what share of US beverage cans have switched.',
          },
          {
            question: 'Are beer cans lined with plastic?',
            answer:
              'Yes: every aluminium beer can has an internal polymer coating. It prevents the beer reacting with the aluminium (which would alter the flavour) and protects the metal from corrosion. Beer is mildly acidic and carbonated, so the coating is doing real work. The beverage-can industry is moving to bisphenol-free coatings, but disclosure is poor and the BPA-free share of beer cans is not publicly quantified the way it is for food cans. Bottled beer in glass avoids the lining question entirely.',
          },
          {
            question: 'Are tuna cans lined with plastic?',
            answer:
              'Yes. Tuna and sardine cans are lined like any other steel food can, with a non-BPA epoxy, acrylic, polyester or olefin coating in most US production. Oil-packed fish extracts more from the coating than brine-packed. On the particle question, the best direct evidence is reassuring: micro-Raman analysis of 20 canned sardine and sprat brands found no plastic particles in 16 of them and only 1–3 in the others. The stronger concern with canned fish is what the fish itself carried, not the tin.',
          },
          {
            question: 'What are acrylic can linings?',
            answer:
              'Acrylic can linings are coatings made from acrylic resin, typically derived from ethyl acrylate. They are one of the four families the Can Manufacturers Institute names as standard BPA replacements, alongside polyester, non-BPA epoxy and olefin polymers. Acrylics resist sulphide staining and look clean, but they are more brittle than epoxy, can affect taste and odour, and cost around twice as much, which is why they make up roughly half the can-coating market by revenue despite a smaller share by volume. They contain no bisphenol backbone, but independent particle-release data on them is essentially non-existent.',
          },
          {
            question: 'Are milk cartons lined with plastic?',
            answer:
              'Yes. An aseptic carton such as a Tetra Pak is a laminate of roughly 70% paperboard, 25% plastic and 5% aluminium foil, built in six layers, and the innermost layer that touches the milk is polyethylene. So cartons are not plastic-free. They are, however, usually a better choice than a can for liquids: no bisphenol epoxy, no retort sterilisation at 120°C, and polyethylene at fridge temperature is one of the lowest-migration plastic-food contacts there is.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'European Food Safety Authority',
            year: 2023,
            title: 'Bisphenol A in food is a health risk: re-evaluation of the risks to public health related to the presence of BPA in foodstuffs',
            publisher: 'EFSA',
            url: 'https://www.efsa.europa.eu/en/news/bisphenol-food-health-risk',
          },
          {
            authors: 'EFSA Panel on Food Contact Materials, Enzymes and Processing Aids (CEP)',
            year: 2023,
            title: 'Re-evaluation of the risks to public health related to the presence of bisphenol A (BPA) in foodstuffs',
            publisher: 'EFSA Journal',
            url: 'https://www.efsa.europa.eu/en/efsajournal/pub/6857',
          },
          {
            authors: 'Can Manufacturers Institute',
            year: 2024,
            title: 'Innovations in Food Cans: can lining materials and the move away from BPA',
            publisher: 'Can Manufacturers Institute',
            url: 'https://www.cancentral.com/innovations-food-cans/',
          },
          {
            authors: 'Food Packaging Forum',
            year: 2025,
            title: 'Can Coatings: background article on epoxy, acrylic, polyester, oleoresin and polyolefin can linings',
            publisher: 'Food Packaging Forum Foundation',
            url: 'https://foodpackagingforum.org/resources/background-articles/can-coatings',
          },
          {
            authors: 'Center for Environmental Health',
            year: 2021,
            title: 'Is Canned Food Safe from BPA Now?',
            publisher: 'Center for Environmental Health',
            url: 'https://ceh.org/yourhealth/is-canned-food-safe-from-bpa-now/',
          },
          {
            authors: 'Carwile JL, Ye X, Zhou X, Calafat AM, Michels KB',
            year: 2011,
            title: 'Canned Soup Consumption and Urinary Bisphenol A: A Randomized Crossover Trial',
            publisher: 'JAMA',
            url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC3367259/',
          },
          {
            authors: 'Rochester JR, Bolden AL',
            year: 2015,
            title: 'Bisphenol S and F: A Systematic Review and Comparison of the Hormonal Activity of Bisphenol A Substitutes',
            publisher: 'Environmental Health Perspectives',
            url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC4492270/',
          },
          {
            authors: 'Zimmermann L, Geueke B, Parkinson LV, Schür C, Wagner M, Muncke J',
            year: 2025,
            title: 'Food contact articles as source of micro- and nanoplastics: a systematic evidence map',
            publisher: 'npj Science of Food',
            url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC12198380/',
          },
          {
            authors: 'Karami A, Golieskardi A, Choo CK, Larat V, Karbalaei S, Salamatinia B',
            year: 2018,
            title: 'Microplastic and mesoplastic contamination in canned sardines and sprats',
            publisher: 'Science of the Total Environment',
            url: 'https://www.sciencedirect.com/science/article/abs/pii/S0048969717323471',
          },
          {
            authors: 'European Commission',
            year: 2024,
            title: 'Commission Regulation (EU) 2024/3190 on the use of bisphenol A and other bisphenols in food contact materials',
            publisher: 'EUR-Lex',
            url: 'https://eur-lex.europa.eu/eli/reg/2024/3190/oj/eng',
          },
          {
            authors: 'US Food and Drug Administration',
            year: 2024,
            title: 'Bisphenol A (BPA): Use in Food Contact Application',
            publisher: 'FDA',
            url: 'https://www.fda.gov/food/food-packaging-other-substances-come-contact-food-information-consumers/bisphenol-bpa-use-food-contact-application',
          },
          {
            authors: 'Tetra Pak',
            year: 2025,
            title: 'Food protection: the layers of an aseptic carton (paperboard, polyethylene, aluminium foil)',
            publisher: 'Tetra Pak',
            url: 'https://www.tetrapak.com/en-us/sustainability/acting-for-sustainability/the-pack-that-protects/food-protection',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
